import {Component, type ErrorInfo, type PropsWithChildren} from 'react';

import {Bugsnag} from './bugsnag';
import {DefaultErrorFallback} from './DefaultErrorFallback';

interface ErrorBoundaryProps {
  fallback?: React.ComponentType<{error: Error}>;
}

interface ErrorBoundaryState {
  error: Error | null;
  hasError: boolean;
}

export class ErrorBoundary extends Component<
  PropsWithChildren<ErrorBoundaryProps>,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {error: null, hasError: false};
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {error, hasError: true};
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to Bugsnag
    Bugsnag.notify(error, event => {
      event.addMetadata('react', {
        componentStack: errorInfo.componentStack,
      });
    });
  }

  override render() {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;

      return <FallbackComponent error={this.state.error} />;
    }

    return this.props.children;
  }
}
